import type {
  CreatePostInput,
  UpdatePostInput,
  PublishPostInput,
  PaginationQuery,
  FontResponse,
  EmailPaginationQuery,
  CreateEmailInput,
  UpdateEmailInput,
  TestSendEmailInput,
  CreateEmailSenderInput,
  UpdateEmailSenderInput,
  EmailSenderList,
  EmailSender,
  EmailList,
  EmailListList,
  CreateEmailListInput,
  UpdateEmailListInput,
  EmailListAttributesResult,
  Subscriber,
  CreateSubscriberInput,
  UpdateSubscriberInput,
  PaginatedSubscribers,
  ImportSubscribersInput,
  ImportSubscribersResult,
  CreateEmailSendInput,
  CreateEmailSendResult,
  CsvSendResult,
  EmailSend,
  EmailSendPreview,
  EmailSendPreviewRequest,
  EmailSendRecipientContent,
  PaginatedEmailSends,
  PaginatedEmailSendRecipients,
  PaginatedUnsubscribes,
  CreateComponentInput,
  UpdateComponentInput,
  ComponentApplyUpdateInput,
  AiDraftRequest,
  AiDraftResult,
  AiProvidersResult,
  WorkspacesResult,
  WorkspaceSummary,
  CreateWorkspaceInput,
  WorkspaceMembersResult,
  WorkspaceInvitesResult,
  WorkspaceInviteResult,
  WorkspaceInvitePeek,
  WorkspaceInviteAcceptResult,
  InviteMemberInput,
  WorkspaceRole,
  WorkspaceSetupAction,
  WorkspaceSetupResult,
  WorkspaceSetupState,
  WorkspaceAttributeCache,
  ApiKey,
  ApiKeyList,
  CreateApiKeyInput,
  CreateApiKeyResult,
  BillingOverview,
  BillingTransactionsResult,
  CreditTransactionType,
  CreditPackagesResult,
  PurchaseCheckoutRequest,
  PurchaseCheckoutResult,
  VerifyPurchaseRequest,
  VerifyPurchaseResult,
  PlansResult,
  InvoiceKind,
  BillingInvoicesResult,
  StartTrialResult,
  SubscriptionCheckoutRequest,
  SubscriptionCheckoutResult,
  Entitlement,
  VariableDefinitions,
  VariableDefinitionsInput,
  DocumentDesign,
  ChangePasswordInput,
  User,
  InstanceSetupInput,
  InstanceSetupResult,
  InstanceSetupStatus,
  AdminCreateUserInput,
  AdminCreateUserResult,
  AdminOverview,
  AdminPaginationQuery,
  AdminSendMetricsOverview,
  AdminSendMetricsQuery,
  AdminSendMetricsResult,
  AdminSetWorkspacePlanInput,
  AdminSetWorkspacePlanResult,
  AdminUsersResult,
  AdminWorkspacesResult,
  SkillList,
  SkillSummary,
  EmailCalendar,
  EmailCalendarInput,
} from '@blogforge/shared';
import { SERVER_URL } from '../lib/constants';
import { useAuthStore } from '../stores/authStore.js';
import type { SenderMetrics } from 'src/lib/sender-metrics.js';

const BASE = SERVER_URL + '/api';

/** `?page=&limit=&search=` builder for the paginated list endpoints. */
function paginationQuery(query: Partial<AdminPaginationQuery>): string {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.limit) params.set('limit', String(query.limit));
  if (query.search) params.set('search', query.search);
  return params.toString();
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const activeWorkspaceId = useAuthStore.getState().activeWorkspaceId;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };
  if (activeWorkspaceId) {
    headers['X-Workspace-Id'] = activeWorkspaceId;
  }

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include', // send httpOnly access_token cookie
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body.error ?? 'Request failed', body);
  }

  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Parsed response body — e.g. { error, availableMicroCredits } for 402s. */
    public body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = {
  auth: {
    login: (email: string, password: string) =>
      request<{ user: User; refreshToken: string }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    logout: () => request('/auth/logout', { method: 'POST' }),
    me: () => request<User>('/auth/me'),
    refresh: (refreshToken: string) =>
      request<{ refreshToken: string }>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),
    /**
     * Change your own password. Pass the session's refresh token so this
     * browser stays signed in — every *other* refresh session is revoked.
     */
    changePassword: (data: ChangePasswordInput, refreshToken?: string | null) =>
      request<{ ok: boolean; revokedSessions: number }>('/auth/password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
          ...(refreshToken ? { refreshToken } : {}),
        }),
      }),
  },

  setup: {
    status: () => request<InstanceSetupStatus>('/setup/status'),
    /** Claim the instance: creates the first platform admin + their workspace. */
    create: (data: InstanceSetupInput) =>
      request<InstanceSetupResult>('/setup', { method: 'POST', body: JSON.stringify(data) }),
  },

  // ── Platform administration ───────────────────────────────────
  admin: {
    overview: () => request<AdminOverview>('/admin/overview'),
    users: (query: Partial<AdminPaginationQuery> = {}) =>
      request<AdminUsersResult>(`/admin/users?${paginationQuery(query)}`),
    /** Create an account; it always owns the workspace created with it. */
    createUser: (data: AdminCreateUserInput) =>
      request<AdminCreateUserResult>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    workspaces: (query: Partial<AdminPaginationQuery> = {}) =>
      request<AdminWorkspacesResult>(`/admin/workspaces?${paginationQuery(query)}`),
    /** Assign a plan without payment ('free' cancels the live subscription). */
    setPlan: (workspaceId: string, data: AdminSetWorkspacePlanInput) =>
      request<AdminSetWorkspacePlanResult>(`/admin/workspaces/${workspaceId}/plan`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    /** Deployment-wide send + recipient totals, and the enforced throttle. */
    sendsOverview: () => request<AdminSendMetricsOverview>('/admin/sends/overview'),
    /** Per-send progress. Counts and a timeline — never recipients or bodies. */
    sends: (query: Partial<AdminSendMetricsQuery> = {}) => {
      const params = new URLSearchParams();
      if (query.page) params.set('page', String(query.page));
      if (query.limit) params.set('limit', String(query.limit));
      if (query.workspaceId) params.set('workspaceId', query.workspaceId);
      if (query.status) params.set('status', query.status);
      return request<AdminSendMetricsResult>(`/admin/sends?${params.toString()}`);
    },
  },

  // ── Emails ─────────────────────────────────────────────────────
  emails: {
    list: (query: Partial<EmailPaginationQuery> = {}) => {
      const params = new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)]),
      );
      return request(`/emails?${params}`);
    },
    get: (id: string) => request(`/emails/${id}`),
    create: (data: CreateEmailInput) =>
      request('/emails', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: UpdateEmailInput) =>
      request(`/emails/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    /**
     * Replaces the email's whole variable-definition record. The generic update
     * route strips `variableDefinition`, so variables only travel through here.
     */
    updateVariables: (id: string, data: VariableDefinitionsInput) =>
      request<{ variableDefinition: VariableDefinitions }>(`/emails/${id}/variables`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    /** Writes the email's whole calendar-invite record (see EmailCalendarSchema). */
    updateCalendarInvite: (id: string, data: EmailCalendarInput) =>
      request<{ calendarInvite: EmailCalendar }>(`/emails/${id}/calendar-invite`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    /** Drops the invite: the email is sent without an event afterwards. */
    deleteCalendarInvite: (id: string) =>
      request<{ ok: boolean }>(`/emails/${id}/calendar-invite`, {
        method: 'DELETE',
        body: '{}',
      }),

    build: (id: string) => request(`/emails/${id}/build`, { method: 'POST', body: '{}' }),
    /**
     * The resolved design the build will use (token cascade + column width), so
     * the canvas can preview the email's own type and colour. See
     * GET /api/emails/:id/design.
     */
    design: (id: string) => request<DocumentDesign>(`/emails/${id}/design`),
    testSend: (id: string, data: TestSendEmailInput) =>
      request(`/emails/${id}/test-send`, { method: 'POST', body: JSON.stringify(data) }),
    delete: (id: string) => request(`/emails/${id}`, { method: 'DELETE', body: '{}' }),
  },

  // ── Email senders (workspace delivery identities) ───────────────
  emailSenders: {
    list: () => request<EmailSenderList>('/emails/senders'),
    get: (id: string) => request<EmailSender>(`/emails/senders/${id}`),
    getMetrics: (id: string) => request<SenderMetrics>(`/emails/senders/${id}/metrics`),
    create: (data: CreateEmailSenderInput) =>
      request<EmailSender>('/emails/senders', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: UpdateEmailSenderInput) =>
      request<EmailSender>(`/emails/senders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    remove: (id: string) => request(`/emails/senders/${id}`, { method: 'DELETE' }),
  },

  // ── Email lists (audience) + subscribers ───────────────────────
  emailLists: {
    list: () => request<EmailListList>('/email-lists'),
    get: (id: string) => request<EmailList>(`/email-lists/${id}`),
    create: (data: CreateEmailListInput) =>
      request<EmailList>('/email-lists', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: UpdateEmailListInput) =>
      request<EmailList>(`/email-lists/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    remove: (id: string) => request(`/email-lists/${id}`, { method: 'DELETE' }),
    /**
     * Replace the list's audience attribute definitions wholesale (the same
     * contract as `emails.updateVariables`). Answers with the rebuilt
     * workspace attribute cache, so the `{{…}}` picker updates from the write
     * that changed it.
     */
    updateAttributes: (id: string, data: VariableDefinitionsInput) =>
      request<EmailListAttributesResult>(`/email-lists/${id}/attributes`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    subscribers: (
      listId: string,
      query: { page?: number; limit?: number; search?: string } = {},
    ) => {
      const params = new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)]),
      );
      return request<PaginatedSubscribers>(`/email-lists/${listId}/subscribers?${params}`);
    },
    addSubscriber: (listId: string, data: CreateSubscriberInput) =>
      request<Subscriber>(`/email-lists/${listId}/subscribers`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateSubscriber: (listId: string, subscriberId: string, data: UpdateSubscriberInput) =>
      request<Subscriber>(`/email-lists/${listId}/subscribers/${subscriberId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    removeSubscriber: (listId: string, subscriberId: string) =>
      request(`/email-lists/${listId}/subscribers/${subscriberId}`, { method: 'DELETE' }),
    /** Import CSV text; returns a per-row summary of what happened. */
    import: (listId: string, data: ImportSubscribersInput) =>
      request<ImportSubscribersResult>(`/email-lists/${listId}/import`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // ── Opt-outs (who left, and why) ───────────────────────────────
  // Read-only: opt-outs are recorded by the public unsubscribe page, never from
  // the editor. `listId` narrows to one list's opt-outs; `scope: 'workspace'`
  // selects the people who asked this sender to stop everything.
  unsubscribes: {
    list: (
      query: {
        page?: number;
        limit?: number;
        listId?: string;
        scope?: 'list' | 'workspace';
      } = {},
    ) => {
      const params = new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)]),
      );
      return request<PaginatedUnsubscribes>(`/unsubscribes?${params}`);
    },
  },

  // ── Email sends (queue an email to an audience) ────────────────
  emailSends: {
    /** Send history, newest first. */
    list: (query: { page?: number; limit?: number; emailId?: string } = {}) => {
      const params = new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)]),
      );
      return request<PaginatedEmailSends>(`/email-sends?${params}`);
    },
    get: (id: string) => request<EmailSend>(`/email-sends/${id}`),
    recipients: (id: string, query: { page?: number; limit?: number; status?: string } = {}) => {
      const params = new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)]),
      );
      return request<PaginatedEmailSendRecipients>(`/email-sends/${id}/recipients?${params}`);
    },
    /**
     * The copy one recipient was sent, fetched per recipient when somebody
     * opens it — a whole rendered email per row would make the list enormous.
     * `contentSnapshot` is null when nothing was kept.
     */
    recipientContent: (id: string, recipientId: string) =>
      request<EmailSendRecipientContent>(`/email-sends/${id}/recipients/${recipientId}/content`),
    /**
     * Queue a send: the server snapshots the selected lists' subscribers into
     * the send as queued recipients and the background sender job delivers
     * them. Nothing is sent by this request.
     */
    create: (data: CreateEmailSendInput) =>
      request<CreateEmailSendResult>('/email-sends', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    /** What a selection resolves to, before anything is queued. */
    preview: (data: EmailSendPreviewRequest) =>
      request<EmailSendPreview>('/email-sends/preview', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    cancel: (id: string, force = false) =>
      request<EmailSend>(`/email-sends/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ force }),
      }),

    /**
     * Put a cancelled send back in the queue.
     *
     * Cancelling never removes recipients — delivered ones stay delivered, the
     * rest stay queued — so this delivers the remainder and nothing else: no
     * one who already has a copy is sent a second one, and anyone who
     * unsubscribed while it was stopped stays off it.
     */
    resume: (id: string) =>
      request<EmailSend>(`/email-sends/${id}/resume`, { method: 'POST', body: '{}' }),

    /**
     * Queue a send whose audience is an uploaded CSV.
     *
     * Sent as multipart so the file never has to travel as JSON text: the same
     * body the public API's `POST /api/v1/sends/csv` accepts (see
     * apps/server/src/email/csv-send.ts). The server validates the whole file
     * before queueing anything and answers with the audience it built, so the
     * UI can report rows, collapses and the temporary list in one place.
     */
    sendCsv: (
      file: File,
      fields: {
        emailId: string;
        senderId: string;
        scheduledAt?: string | null;
        fromName?: string;
        fromAddress?: string;
        /** Where replies go; omitted leaves them on the From address. */
        replyTo?: string;
      },
    ) => {
      const form = new FormData();
      form.append('file', file);
      form.append('emailId', fields.emailId);
      form.append('senderId', fields.senderId);
      if (fields.scheduledAt) form.append('scheduledAt', fields.scheduledAt);
      if (fields.fromName) form.append('fromName', fields.fromName);
      if (fields.fromAddress) form.append('fromAddress', fields.fromAddress);
      if (fields.replyTo) form.append('replyTo', fields.replyTo);

      const activeWorkspaceId = useAuthStore.getState().activeWorkspaceId;
      return fetch(`${BASE}/email-sends/csv`, {
        method: 'POST',
        body: form,
        credentials: 'include',
        ...(activeWorkspaceId ? { headers: { 'X-Workspace-Id': activeWorkspaceId } } : {}),
      }).then(async (res) => {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        if (!res.ok) throw new ApiError(res.status, body.error ?? 'Upload failed', body);
        return body as CsvSendResult;
      });
    },
  },

  // ── Components (reusable content across posts/emails) ──────────
  components: {
    list: (
      query: {
        page?: number | undefined;
        limit?: number | undefined;
        search?: string | undefined;
        target?: string | undefined;
      } = {},
    ) => {
      const params = new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)]),
      );
      return request(`/components?${params}`);
    },
    get: (id: string) => request(`/components/${id}`),
    create: (data: CreateComponentInput) =>
      request('/components', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: UpdateComponentInput) =>
      request(`/components/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) => request(`/components/${id}`, { method: 'DELETE', body: '{}' }),
    usages: (id: string) => request(`/components/${id}/usages`),
    apply: (id: string, data: ComponentApplyUpdateInput) =>
      request(`/components/${id}/apply`, { method: 'POST', body: JSON.stringify(data) }),
  },

  // ── Posts ─────────────────────────────────────────────────────
  posts: {
    list: (query: Partial<PaginationQuery> = {}) => {
      const params = new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)]),
      );
      return request(`/posts?${params}`);
    },
    get: (id: string) => request(`/posts/${id}`),
    create: (data: CreatePostInput) =>
      request('/posts', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: UpdatePostInput) =>
      request(`/posts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    publish: (id: string, data: PublishPostInput = {}) =>
      request(`/posts/${id}/publish`, { method: 'POST', body: JSON.stringify(data) }),
    preview: (id: string) => request(`/posts/${id}/preview`, { method: 'POST', body: '{}' }),
    /** Resolved design the build will use — see GET /api/posts/:id/design. */
    design: (id: string) => request<DocumentDesign>(`/posts/${id}/design`),
    delete: (id: string) => request(`/posts/${id}`, { method: 'DELETE', body: '{}' }),
  },

  // ── Assets ────────────────────────────────────────────────────
  assets: {
    list: () => request('/assets'),
    upload: (file: File, variables?: Record<string, string> | undefined) => {
      const form = new FormData();

      if (variables) {
        for (const [key, val] of Object.entries(variables)) {
          form.append(key, val);
        }
      }

      form.append('file', file);

      return fetch(`${BASE}/assets/upload`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      }).then((r) => r.json());
    },
    delete: (id: string) => request(`/assets/${id}`, { method: 'DELETE' }),
  },

  // ── Settings ──────────────────────────────────────────────────
  settings: {
    getSite: () => request('/settings/site'),
    updateSite: (data: unknown) =>
      request('/settings/site', { method: 'PUT', body: JSON.stringify(data) }),
    getThemes: () => request('/settings/themes'),
    getPlugins: () => request('/settings/plugins'),
  },

  // ── Tags ──────────────────────────────────────────────────────
  tags: {
    list: () => request('/tags'),
    create: (name: string) => request('/tags', { method: 'POST', body: JSON.stringify({ name }) }),
  },

  // ── AI content generation ─────────────────────────────────────
  ai: {
    /** Implemented providers + models + defaults — feeds the model picker. */
    providers: () => request<AiProvidersResult>('/ai/providers'),
    /** Draft a post/email from a brief. Pass an AbortSignal to cancel. */
    drafts: (body: AiDraftRequest, signal?: AbortSignal) =>
      request<AiDraftResult>('/ai/drafts', {
        method: 'POST',
        body: JSON.stringify(body),
        ...(signal ? { signal } : {}),
      }),
  },

  // ── Fonts ─────────────────────────────────────────────────────
  fonts: {
    list: () => request<FontResponse[]>('/fonts'),
  },

  // ── Portable skills (downloadable instructions for an AI) ─────
  // The catalog and the files it describes. Both skills also ship in the
  // repository; see apps/server/src/skills/.
  skills: {
    /** What this deployment offers, with its version, size and how-to copy. */
    list: () => request<SkillList>('/skills'),
    /**
     * Fetch one skill's markdown and save it.
     *
     * A blob download rather than a plain link because the file sits behind
     * the session cookie and the editor is a different origin in dev — a bare
     * `<a href>` would arrive unauthenticated there, and the failure would
     * look like a broken button.
     */
    download: async (skill: SkillSummary): Promise<void> => {
      const res = await fetch(`${BASE}/skills/${skill.id}/download`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new ApiError(res.status, body.error ?? 'Download failed', body);
      }

      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = skill.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  },

  // ── Workspaces (credit/billing boundary) ──────────────────────
  workspaces: {
    /** Workspaces the signed-in user belongs to — drives the switcher. */
    list: () => request<WorkspacesResult>('/workspaces'),
    /** Create a workspace; the caller becomes its owner. */
    create: (name: string) =>
      request<WorkspaceSummary>('/workspaces', {
        method: 'POST',
        body: JSON.stringify({ name } satisfies CreateWorkspaceInput),
      }),
    /** Members of the active workspace. */
    members: (workspaceId: string) =>
      request<WorkspaceMembersResult>(`/workspaces/${workspaceId}/members`),
    /** Rename a workspace (owner/admin). */
    rename: (workspaceId: string, name: string) =>
      request<WorkspaceSummary>(`/workspaces/${workspaceId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name } satisfies CreateWorkspaceInput),
      }),
    /** Overview of one workspace (role, member and invite counts). */
    get: (workspaceId: string) => request<WorkspaceSummary>(`/workspaces/${workspaceId}`),
    /**
     * The workspace's cached audience attribute keys, grouped by list. Read-only
     * — attributes are edited per list (see `emailLists.updateAttributes`).
     */
    attributes: (workspaceId: string) =>
      request<WorkspaceAttributeCache>(`/workspaces/${workspaceId}/attributes`),
    /** Invite a person by email (returns the copyable accept link). */
    invite: (workspaceId: string, data: InviteMemberInput) =>
      request<WorkspaceInviteResult>(`/workspaces/${workspaceId}/members`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    /** Outstanding (unaccepted) invites. */
    invites: (workspaceId: string) =>
      request<WorkspaceInvitesResult>(`/workspaces/${workspaceId}/invites`),
    /** Change a member's role. */
    changeRole: (workspaceId: string, userId: string, role: WorkspaceRole) =>
      request(`/workspaces/${workspaceId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    /** Remove a member. */
    removeMember: (workspaceId: string, userId: string) =>
      request(`/workspaces/${workspaceId}/members/${userId}`, { method: 'DELETE' }),
    /** Public peek for the accept-invite screen. */
    invitePeek: (token: string) => request<WorkspaceInvitePeek>(`/workspaces/invites/${token}`),
    /** Redeem an invite for the signed-in user. */
    acceptInvite: (token: string) =>
      request<WorkspaceInviteAcceptResult>(`/workspaces/invites/accept`, {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),

    // ── API access (workspace credentials) ────────────────────────
    /**
     * Keys this workspace has issued, revoked ones included. The secret is
     * never in a response: it exists only in the create result below.
     */
    apiKeys: (workspaceId: string) => request<ApiKeyList>(`/workspaces/${workspaceId}/api-keys`),
    /**
     * Create a scoped key pair. The response is the ONE place the secret
     * appears — nothing can read it back afterwards, so the UI must show it
     * once and let the user copy it.
     */
    createApiKey: (workspaceId: string, data: CreateApiKeyInput) =>
      request<CreateApiKeyResult>(`/workspaces/${workspaceId}/api-keys`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    /** Revoke a key: it stops authenticating immediately, the row stays. */
    revokeApiKey: (workspaceId: string, keyId: string) =>
      request<{ ok: boolean; apiKey: ApiKey }>(`/workspaces/${workspaceId}/api-keys/${keyId}`, {
        method: 'DELETE',
      }),

    // ── Onboarding ────────────────────────────────────────────────
    /** Everything the setup wizard prefills from (owner/admin). */
    setup: (workspaceId: string) =>
      request<WorkspaceSetupState>(`/workspaces/${workspaceId}/setup`),
    /** Finish ('complete') or dismiss ('skip') the wizard. */
    setupAction: (workspaceId: string, action: WorkspaceSetupAction) =>
      request<WorkspaceSetupResult>(`/workspaces/${workspaceId}/setup`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      }),
  },

  // ── Billing / credits ─────────────────────────────────────────
  billing: {
    /** Balance + subscription + recent ledger entries (active workspace). */
    overview: () => request<BillingOverview>('/billing/overview'),
    transactions: (query: { page?: number; limit?: number; type?: CreditTransactionType } = {}) => {
      const params = new URLSearchParams();
      if (query.page) params.set('page', String(query.page));
      if (query.limit) params.set('limit', String(query.limit));
      if (query.type) params.set('type', query.type);
      return request<BillingTransactionsResult>(`/billing/transactions?${params}`);
    },
    /** Credit packages available to buy. */
    packages: () => request<CreditPackagesResult>('/billing/packages'),
    /** Plan limits/features + live usage for the active workspace. */
    entitlement: () => request<Entitlement>('/billing/entitlement'),
    /** Current subscription + tier catalog + trial/term info. */
    plans: () => request<PlansResult>('/billing/plans'),
    /** Subscription purchasing (Individual, no auto-renew). */
    subscriptions: {
      /** Start the free trial (owner, one per workspace/user). */
      startTrial: () =>
        request<StartTrialResult>('/billing/subscriptions/start-trial', {
          method: 'POST',
          body: '{}',
        }),
      /** Start a paid term checkout (30 days); returns Paystack authorizationUrl. */
      checkout: (data: SubscriptionCheckoutRequest) =>
        request<SubscriptionCheckoutResult>('/billing/subscriptions/checkout', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
    },
    /** Invoice history for both kinds (credit top-ups + subscription charges). */
    invoices: (query: { page?: number; limit?: number; kind?: InvoiceKind } = {}) => {
      const params = new URLSearchParams();
      if (query.page) params.set('page', String(query.page));
      if (query.limit) params.set('limit', String(query.limit));
      if (query.kind) params.set('kind', query.kind);
      return request<BillingInvoicesResult>(`/billing/invoices?${params}`);
    },
    purchases: {
      /** Start a Paystack checkout; returns the hosted authorizationUrl. */
      create: (data: PurchaseCheckoutRequest) =>
        request<PurchaseCheckoutResult>('/billing/purchases', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      /** Verify + settle after Paystack redirects the customer back. */
      verify: (reference: string) =>
        request<VerifyPurchaseResult>('/billing/purchases/verify', {
          method: 'POST',
          body: JSON.stringify({ reference } satisfies VerifyPurchaseRequest),
        }),
    },
  },
};
